/**
 * LandingBacktestCompare — interactive mini-backtester embedded directly on
 * the landing page. Fires N parallel `/api/backtest` calls (one per ticker
 * plus a benchmark, each with a single-holding portfolio at 100% weight)
 * so we can render true per-ticker time series and a per-ticker breakdown
 * table (Start Price / End Price / Price Return / Dividends / Dividend
 * Return / Total Return / Ending Value).
 *
 * All backtest responses are cached server-side by input-hash so repeat
 * visits + micro-tweaks (Cash <-> DRIP, horizon changes) return instantly
 * after the first cold run. Anonymous users are supported by the endpoint.
 *
 * Signed-in visitors get a one-click "Ask DPP about these results" CTA
 * that hands the summary to /research (Pro AI Chat). Anonymous visitors
 * see the follow-up questions render as read-only pills that funnel to
 * /auth?mode=register.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, X, Sparkles, ArrowRight } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import api from "@/lib/api";

const DEFAULT_TICKERS = ["MFA", "RITM", "EFC", "AGNC", "ET"];
const DEFAULT_BENCHMARK = "VOO";

// One-tap preset baskets. Each preset swaps the entire ticker list (plus
// benchmark if the theme benefits from a different one) so visitors can
// A/B different dividend strategies without typing. `Featured` matches
// the current default state so a visitor can always get back home.
const PRESETS = [
  {
    id: "featured",
    label: "Featured",
    blurb: "High-yield REIT & energy mix",
    tickers: DEFAULT_TICKERS,
    benchmark: DEFAULT_BENCHMARK,
  },
  {
    id: "high-yield-etfs",
    label: "High-Yield ETFs",
    blurb: "Covered-call & premium income ETFs",
    tickers: ["JEPI", "JEPQ", "SCHD", "QYLD", "SPYI"],
    benchmark: "VOO",
  },
  {
    id: "dividend-aristocrats",
    label: "Dividend Aristocrats",
    blurb: "25+ year dividend growers",
    tickers: ["KO", "PG", "JNJ", "MMM", "PEP"],
    benchmark: "NOBL",
  },
  {
    id: "monthly-payers",
    label: "Monthly Payers",
    blurb: "Stocks & ETFs that pay every month",
    tickers: ["O", "MAIN", "STAG", "AGNC", "SLG"],
    benchmark: "VOO",
  },
];

// Colorblind-friendly palette; benchmark always renders as sky blue for
// consistency, other tickers cycle through the accent set.
const COLORS = ["#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#14b8a6", "#eab308"];
const BENCHMARK_COLOR = "#0ea5e9";

const HORIZONS = [
  { label: "3M",  months: 3  },
  { label: "6M",  months: 6  },
  { label: "12M", months: 12 },
  { label: "18M", months: 18 },
  { label: "3Y",  months: 36 },
  { label: "5Y",  months: 60 },
];

function monthsAgoISO(months) {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return d.toISOString().slice(0, 10);
}
function todayISO() { return new Date().toISOString().slice(0, 10); }

const fmt$ = (v) => v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (v) => v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;

/** Run one anonymous backtest for a single-ticker "portfolio" at 100% weight.
 *  Cached server-side so repeat requests are effectively free. */
async function runOne(ticker, initial, startISO, endISO, drip) {
  const body = {
    name: `Landing preview — ${ticker}`,
    holdings: [{ ticker, weight: 100 }],
    initial_investment: initial,
    start_date: startISO,
    end_date: endISO,
    drip,
  };
  const r = await api.post("/backtest", body);
  return r.data;
}

/** Derive per-ticker start/end prices from the CASH backtest result.
 *  With drip=false shares are constant, so start_price = initial / shares
 *  and end_price = current_value / shares. */
function extractPrices(cashResult, initial) {
  const c = cashResult?.contributions?.[0];
  if (!c || !c.shares || c.shares <= 0) return { startPrice: null, endPrice: null };
  const startPrice = initial / c.shares;
  const endPrice = c.current_value / c.shares;
  return { startPrice, endPrice };
}

export default function LandingBacktestCompare() {
  const nav = useNavigate();
  const [tickers, setTickers] = useState(DEFAULT_TICKERS);
  const [benchmark, setBenchmark] = useState(DEFAULT_BENCHMARK);
  const [investment, setInvestment] = useState(10000);
  const [months, setMonths] = useState(12);
  const [drip, setDrip] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState({});      // { ticker: { cash: BacktestResult, drip: BacktestResult|null } }
  const [addValue, setAddValue] = useState("");
  const [activePreset, setActivePreset] = useState("featured");

  const applyPreset = (preset) => {
    setActivePreset(preset.id);
    setTickers(preset.tickers);
    setBenchmark(preset.benchmark);
    // Investment amount + horizon + DRIP intentionally preserved so users
    // can compare the same "$10k over 12 months" story across baskets.
  };

  const startISO = useMemo(() => monthsAgoISO(months), [months]);
  const endISO = useMemo(() => todayISO(), []);

  // Guard against races when the user rapidly toggles horizon / DRIP.
  const runIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const runId = ++runIdRef.current;
    setLoading(true);
    setError("");

    const all = [...tickers, benchmark];
    (async () => {
      try {
        const cashOut = await Promise.all(
          all.map((t) => runOne(t, investment, startISO, endISO, false).then((r) => [t, r]).catch((e) => [t, { __err: e?.response?.data?.detail || "no data" }])),
        );
        if (cancelled || runIdRef.current !== runId) return;
        const combined = {};
        cashOut.forEach(([t, r]) => { combined[t] = { cash: r, drip: null }; });

        // If DRIP is on we also need DRIP totals for the Total Return column.
        if (drip) {
          const dripOut = await Promise.all(
            all.map((t) => runOne(t, investment, startISO, endISO, true).then((r) => [t, r]).catch(() => [t, null])),
          );
          if (cancelled || runIdRef.current !== runId) return;
          dripOut.forEach(([t, r]) => { if (combined[t]) combined[t].drip = r; });
        }

        setResults(combined);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to run backtest");
      } finally {
        if (!cancelled && runIdRef.current === runId) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [tickers, benchmark, investment, startISO, endISO, drip]);

  // Merge N per-ticker time series into a single row-per-date chart dataset
  // Recharts can render. Sample the union of dates from every series.
  const chartData = useMemo(() => {
    const all = [...tickers, benchmark];
    const byDate = new Map();
    all.forEach((t) => {
      const r = results[t]?.cash;
      if (!r?.series) return;
      r.series.forEach((pt) => {
        const row = byDate.get(pt.date) || { date: pt.date };
        row[t] = Math.round(pt.value);
        byDate.set(pt.date, row);
      });
    });
    return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
  }, [results, tickers, benchmark]);

  const rows = useMemo(() => {
    const all = [...tickers, benchmark];
    return all.map((t, i) => {
      const cash = results[t]?.cash;
      const dripRes = results[t]?.drip;
      if (!cash || cash.__err) return { ticker: t, isBench: t === benchmark, err: cash?.__err || null, color: t === benchmark ? BENCHMARK_COLOR : COLORS[i % COLORS.length] };
      const { startPrice, endPrice } = extractPrices(cash, investment);
      const priceReturnPct = startPrice ? ((endPrice / startPrice) - 1) * 100 : null;
      const totalDivs = cash.summary?.total_dividends ?? 0;
      const divReturnPct = investment ? (totalDivs / investment) * 100 : null;
      const active = drip && dripRes ? dripRes : cash;
      return {
        ticker: t,
        isBench: t === benchmark,
        color: t === benchmark ? BENCHMARK_COLOR : COLORS[i % COLORS.length],
        startPrice,
        endPrice,
        priceReturnPct,
        totalDivs,
        divReturnPct,
        totalReturnPct: active.summary?.total_return_pct ?? null,
        endingValue: active.summary?.final_value ?? null,
      };
    });
  }, [results, tickers, benchmark, investment, drip]);

  const addTicker = () => {
    const t = addValue.trim().toUpperCase();
    if (!t) return;
    if (t === benchmark || tickers.includes(t)) { setAddValue(""); return; }
    if (tickers.length >= 8) { setError("Compare up to 8 tickers at once"); return; }
    setTickers((cur) => [...cur, t]);
    setAddValue("");
    setActivePreset(null);   // manual edit ⇒ no preset is "active" anymore
  };
  const removeTicker = (t) => {
    setTickers((cur) => cur.filter((x) => x !== t));
    setActivePreset(null);
  };

  return (
    <section
      className="max-w-7xl mx-auto px-6 py-14"
      data-testid="landing-backtest-compare"
    >
      <div className="mb-4">
        <h2 className="serif text-3xl sm:text-4xl">Total Return Comparison</h2>
        <p className="text-muted-foreground mt-1">See how price appreciation and dividends worked together.</p>
      </div>

      {/* Featured preset baskets — one tap swaps the entire ticker list
          (and benchmark where meaningful) so visitors can flip between
          strategies without typing. Manual edits clear the active preset. */}
      <div
        className="flex flex-wrap items-center gap-2 mb-4"
        data-testid="backtest-presets"
        role="tablist"
        aria-label="Preset baskets"
      >
        <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground mr-1">
          Preset baskets:
        </span>
        {PRESETS.map((p) => {
          const isActive = activePreset === p.id;
          return (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-pressed={isActive}
              onClick={() => applyPreset(p)}
              data-testid={`backtest-preset-${p.id}`}
              title={p.blurb}
              className={
                isActive
                  ? "rounded-full px-3.5 py-1.5 text-sm font-semibold border transition-colors bg-primary text-primary-foreground border-primary shadow-sm"
                  : "rounded-full px-3.5 py-1.5 text-sm font-medium border border-border bg-card text-foreground/80 hover:bg-primary/5 hover:border-primary/40 hover:text-primary transition-colors"
              }
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Ticker chips + Add */}
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="backtest-ticker-chips">
        {tickers.map((t, i) => (
          <span
            key={t}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card pl-3 pr-1 py-1 text-sm shadow-sm"
            style={{ borderColor: `${COLORS[i % COLORS.length]}40` }}
            data-testid={`backtest-chip-${t}`}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
            <span className="mono font-semibold">{t}</span>
            <button
              type="button"
              onClick={() => removeTicker(t)}
              className="w-5 h-5 rounded-full hover:bg-muted grid place-items-center transition-colors"
              aria-label={`Remove ${t}`}
              data-testid={`backtest-remove-${t}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <form
          onSubmit={(e) => { e.preventDefault(); addTicker(); }}
          className="inline-flex items-center gap-1"
          data-testid="backtest-add-form"
        >
          <Input
            value={addValue}
            onChange={(e) => setAddValue(e.target.value.toUpperCase())}
            placeholder="+ Add Ticker"
            className="rounded-full h-9 w-36 text-sm"
            maxLength={6}
            data-testid="backtest-add-input"
          />
          <Button type="submit" size="sm" variant="ghost" className="rounded-full" data-testid="backtest-add-btn">
            <Plus className="w-4 h-4" />
          </Button>
        </form>
      </div>

      {/* Controls row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5" data-testid="backtest-controls">
        <div>
          <Label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Investment Amount</Label>
          <Input
            type="number"
            min={100}
            step={100}
            value={investment}
            onChange={(e) => setInvestment(Math.max(100, Number(e.target.value) || 100))}
            className="mt-1"
            data-testid="backtest-investment"
          />
        </div>
        <div>
          <Label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Time Horizon</Label>
          <div className="mt-1 inline-flex rounded-lg border border-border bg-card p-0.5 flex-wrap">
            {HORIZONS.map((h) => (
              <button
                key={h.label}
                type="button"
                onClick={() => setMonths(h.months)}
                className={`px-3 h-8 text-xs rounded-md transition-colors ${months === h.months ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`backtest-horizon-${h.label}`}
              >
                {h.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Dividends</Label>
          <div className="mt-1 inline-flex rounded-lg border border-border bg-card p-0.5">
            <button
              type="button"
              onClick={() => setDrip(false)}
              className={`px-4 h-8 text-xs rounded-md transition-colors ${!drip ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="backtest-drip-cash"
            >Cash</button>
            <button
              type="button"
              onClick={() => setDrip(true)}
              className={`px-4 h-8 text-xs rounded-md transition-colors ${drip ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="backtest-drip-drip"
            >DRIP</button>
          </div>
        </div>
        <div>
          <Label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Benchmark</Label>
          <Input
            value={benchmark}
            onChange={(e) => setBenchmark(e.target.value.toUpperCase().trim())}
            placeholder="VOO"
            className="mt-1"
            maxLength={6}
            data-testid="backtest-benchmark"
          />
        </div>
      </div>

      {/* Chart */}
      <div className="rounded-xl border border-border bg-card p-4 mb-5 relative" data-testid="backtest-chart">
        <div className="text-sm font-medium mb-2">Total Investment Value (Price + Dividends)</div>
        {loading && (
          <div className="absolute inset-0 grid place-items-center bg-card/60 rounded-xl z-10">
            <div className="inline-flex items-center gap-2 mono text-xs text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Running backtest…
            </div>
          </div>
        )}
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              stroke="currentColor"
              fontSize={11}
              tickFormatter={(d) => {
                const dt = new Date(d);
                return dt.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
              }}
              minTickGap={40}
            />
            <YAxis
              stroke="currentColor"
              fontSize={11}
              tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`}
              domain={["auto", "auto"]}
            />
            <Tooltip
              contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
              formatter={(v) => [`$${Math.round(v).toLocaleString()}`, ""]}
              labelFormatter={(d) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {[...tickers, benchmark].map((t, i) => (
              <Line
                key={t}
                type="monotone"
                dataKey={t}
                stroke={t === benchmark ? BENCHMARK_COLOR : COLORS[i % COLORS.length]}
                strokeWidth={t === benchmark ? 2 : 2}
                strokeDasharray={t === benchmark ? "4 4" : undefined}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {error && <div className="text-sm text-destructive mb-3" role="alert">{error}</div>}

      {/* Per-ticker table */}
      <div className="rounded-xl border border-border overflow-hidden bg-card" data-testid="backtest-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                <th className="text-left px-4 py-3">Ticker</th>
                <th className="text-right px-4 py-3">Start Price</th>
                <th className="text-right px-4 py-3">End Price</th>
                <th className="text-right px-4 py-3">Price Return</th>
                <th className="text-right px-4 py-3">Dividends</th>
                <th className="text-right px-4 py-3">Dividend Return</th>
                <th className="text-right px-4 py-3">Total Return</th>
                <th className="text-right px-4 py-3">Ending Value</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.ticker} className="border-t border-border" data-testid={`backtest-row-${r.ticker}`}>
                  <td className="px-4 py-3 mono font-semibold" style={{ color: r.color }}>
                    {r.ticker}{r.isBench && <span className="ml-1 mono text-[10px] uppercase tracking-widest text-muted-foreground">· benchmark</span>}
                  </td>
                  {r.err ? (
                    <td colSpan={7} className="px-4 py-3 text-xs text-muted-foreground italic">{r.err}</td>
                  ) : (
                    <>
                      <td className="px-4 py-3 mono text-right">{fmt$(r.startPrice)}</td>
                      <td className="px-4 py-3 mono text-right">{fmt$(r.endPrice)}</td>
                      <td className={`px-4 py-3 mono text-right ${r.priceReturnPct == null ? "" : r.priceReturnPct >= 0 ? "text-primary" : "text-destructive"}`}>{fmtPct(r.priceReturnPct)}</td>
                      <td className="px-4 py-3 mono text-right">{fmt$(r.totalDivs)}</td>
                      <td className={`px-4 py-3 mono text-right ${r.divReturnPct == null ? "" : r.divReturnPct >= 0 ? "text-primary" : "text-destructive"}`}>{fmtPct(r.divReturnPct)}</td>
                      <td className={`px-4 py-3 mono text-right font-semibold ${r.totalReturnPct == null ? "" : r.totalReturnPct >= 0 ? "text-primary" : "text-destructive"}`}>{fmtPct(r.totalReturnPct)}</td>
                      <td className="px-4 py-3 mono text-right">{fmt$(r.endingValue)}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Follow-up CTA */}
      <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground max-w-2xl">
          <Sparkles className="w-4 h-4 inline-block mr-1 text-secondary" />
          These are real Yahoo Finance numbers, computed live. Save a scenario to run 30-year backtests, add DRIP compounding, and get AI-written explanations of every result.
        </div>
        <Button
          onClick={() => nav("/portfolio/new?seed=" + encodeURIComponent(tickers.join(",")))}
          className="rounded-full shrink-0"
          data-testid="backtest-open-builder"
        >
          Open the full builder <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </section>
  );
}
